import React, { useEffect, useState } from "react";
import { Truck, Factory, Warehouse, CheckCircle2, XCircle, Clock, IndianRupee, ShieldAlert, Zap } from "lucide-react";
import { api, fmtINR } from "../services/api";

export default function Fulfillment() {
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState("ORD-12345");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.orders().then((d) => setOrders(d.orders)); }, []);

  const run = async (id) => {
    setLoading(true); setResult(null);
    try { setResult(await api.fulfillment({ order_id: id })); }
    finally { setLoading(false); }
  };
  useEffect(() => { run("ORD-12345"); }, []); // eslint-disable-line

  return (
    <div className="ff-page">
      <div className="ff-picker">
        <div className="ff-picker-h">Fulfill an order — DotIQ finds the fastest <b>feasible</b> path</div>
        <div className="ff-order-row">
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)}>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>{o.id} — {o.qty.toLocaleString("en-IN")} {o.sku.includes("SAN") ? "units" : "sqm"} {o.sku_name} → {o.dealer_name}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => run(orderId)} disabled={loading}>
            <Zap size={16} /> {loading ? "Traversing graph…" : "Find best path"}
          </button>
        </div>
      </div>

      {loading && <div className="loading-page">Traversing context graph → candidate paths → constraint filter → scoring…</div>}

      {result && result.best && (
        <>
          <div className="ff-query-line">
            Fulfill <b>{result.order_id}</b>: {result.qty.toLocaleString("en-IN")} {result.sku.name.includes("Sanitary") ? "units" : "sqm"} of <b>{result.sku.name}</b> for <b>{result.dealer.name}</b> ({result.dealer.city}) · priority {result.dealer.priority}
          </div>

          <div className="best-path">
            <div className="best-tag"><CheckCircle2 size={16} /> Recommended fulfillment path</div>
            <div className="best-route">
              <div className="route-node">
                {result.best.type === "Production" ? <Factory size={18} /> : <Warehouse size={18} />}
                <span>{result.best.source_name}</span>
              </div>
              <div className="route-arrow"><Truck size={16} /><span>{result.best.mode}</span></div>
              <div className="route-node dest"><span>{result.dealer.city}</span></div>
            </div>
            <div className="best-stats">
              <Stat icon={Clock} label="Expected delivery" value={`${result.best.delivery_days} days`} accent />
              <Stat icon={Factory} label="Production lead" value={`${result.best.production_days} d`} />
              <Stat icon={ShieldAlert} label="Inventory risk" value={result.best.inventory_risk ? "Yes" : "None"} />
              <Stat icon={IndianRupee} label="Cost index" value={result.best.cost_index} />
            </div>
            <div className="best-why">
              <b>Why selected:</b> Fastest feasible option — {result.best.note || "inventory available, lane capacity confirmed, safety stock preserved after allocation."}
            </div>
          </div>

          <div className="paths-h">All evaluated paths <span>(graph traversal → candidates → constraint filter → weighted scoring)</span></div>
          <div className="paths-list">
            {result.all_paths.map((p, i) => (
              <div key={i} className={`path-row ${p.feasible ? "feasible" : "infeasible"} ${p === result.best ? "is-best" : ""}`}>
                <div className="path-icon">{p.type === "Production" ? <Factory size={16} /> : <Warehouse size={16} />}</div>
                <div className="path-main">
                  <div className="path-title">{p.source_name} <span className="path-type">{p.type}</span>{p === result.best && <span className="best-mini">BEST</span>}</div>
                  <div className="path-metrics">
                    <span>{p.delivery_days}d delivery</span>
                    {p.production_days > 0 && <span>{p.production_days}d produce</span>}
                    <span>score {p.score}</span>
                    <span>lane {p.lane_status}</span>
                  </div>
                  {!p.feasible && p.constraints?.length > 0 && (
                    <div className="path-constraint"><XCircle size={13} /> {p.constraints[0]}</div>
                  )}
                </div>
                <div className="path-flag">
                  {p.feasible ? <span className="flag-ok"><CheckCircle2 size={14} /> Feasible</span>
                    : <span className="flag-no"><XCircle size={14} /> Infeasible</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {result && !result.best && <div className="empty-state">No feasible fulfillment path found for this order with current enterprise state.</div>}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`ff-stat ${accent ? "accent" : ""}`}>
      <Icon size={15} />
      <div><div className="ff-stat-val">{value}</div><div className="ff-stat-lbl">{label}</div></div>
    </div>
  );
}
